import {
  AdControlBannerModel,
  AdControlRequest,
  AdControlResponse,
  AdropParamsFactory,
  adcontrolQueryRequest,
} from '@/entities/advertisement';
import { AdPageType } from '@/models/ad/list';
import { Client } from '@/types/app';
import { QueryClient, useQuery } from '@tanstack/react-query';

const getKey = (props: AdControlRequest) => {
  return ['ad-control', 'banner', props];
};

const createBannerModel = (data: AdControlResponse) => {
  return new AdControlBannerModel({
    response: data,
  });
};

const useAdControlBanner = (
  props: AdControlRequest,
  options?: {
    enabled?: boolean;
    onError?: (error: Error) => void;
  }
) => {
  const enabled = options?.enabled ?? true;

  const {
    dataUpdatedAt,
    refetch,
    data: adModel,
    isInitialLoading: isLoading,
    error,
    isSuccess,
  } = useQuery(
    getKey(props),
    async () => {
      const response = await adcontrolQueryRequest({ ...props, adType: 'banner' as const });
      return response.data as AdControlResponse;
    },
    {
      enabled,
      staleTime: 1000 * 5,
      cacheTime: 1000 * 5,
      select: createBannerModel,
      onError: options?.onError,
    }
  );

  return {
    adModel,
    data: adModel,
    isLoading,
    error,
    isSuccess,
    isVisible: adModel?.isVisible || false,
    refetch,
    dataUpdatedAt,
  };
};

const prefetchAdControlBanner = async (
  queryClient: QueryClient,
  adPageType: AdPageType,
  client: Client,
  contextId?: string
) => {
  const params = new AdropParamsFactory(adPageType, client);

  const unitId = params.getUnitId();
  if (!unitId) {
    return;
  }

  const props: AdControlRequest = {
    unitId: unitId,
    platform: params.getPlatform(),
    locale: 'ko_KR',
    contextId: contextId,
  };

  await queryClient.prefetchQuery(
    getKey(props),
    async () => {
      const response = await adcontrolQueryRequest({ ...props, adType: 'banner' as const });
      return response.data as AdControlResponse;
    },
    {
      staleTime: 1000 * 5,
      cacheTime: 1000 * 5,
    }
  );
};

useAdControlBanner.prefetchQuery = prefetchAdControlBanner;
useAdControlBanner.getKey = getKey;

export default useAdControlBanner;
